import { useState } from "react";

import type {
  GQLResendWelcomeMailV2Success,
  GQLResendWelcomeMailV2Failure,
} from "@rx-cs-system/schema";
import type { ResendWelcomeMailLayoutProps } from "../../../components/pages/medicalInstitution/ResendWelcomeMailLayout";
import { ResendSecretCodeWelcomeMailPage } from "../../../components/pages/medicalInstitution/ResendSecretCodeWelcomeMailPage";
import { ResendIntegrationWelcomeMailPage } from "../../../components/pages/medicalInstitution/ResendIntegrationWelcomeMailPage";
import { Loading } from "../../../components/atoms/Loading";
import { useMutation } from "@apollo/client/react";
import { GQLMutation } from "@rx-cs-system/schema";
import { RESEND_WELCOME_MAIL_V2 } from "../../../graphql/mutations";
import { Navigate, useNavigate, useParams } from "react-router";
import { useResendWelcomeMailContract } from "./useResendWelcomeMailContract";

const isFailure = (res: any): res is GQLResendWelcomeMailV2Failure =>
  res.__typename === "ResendWelcomeMailV2Failure";

export const ResendWelcomeMailV2Page = () => {
  const { id: medicalInstitutionId, contractId } = useParams<{
    id: string;
    contractId: string;
  }>();
  if (!medicalInstitutionId || !contractId)
    throw new Error("medicalInstitutionId or contractId is undefined");

  const [snackBar, setSnackBar] =
    useState<ResendWelcomeMailLayoutProps["snackBar"]>(null);

  const [selectedMemberId, setSelectedMember] = useState<string | undefined>(
    undefined,
  );
  const [reissueIntegrationToken, setReissueIntegrationToken] = useState(false);

  const showSnackBar = (type: "ok" | "error", message: string) => {
    setSnackBar({
      type,
      message,
      isOpen: true,
      close() {
        setSnackBar(null);
      },
    });
  };

  const [resendWelcomeEmail, { loading: resendWelcomeMailProcessing }] =
    useMutation<{
      resendWelcomeMailV2?: GQLMutation["resendWelcomeMailV2"];
    }>(RESEND_WELCOME_MAIL_V2, {
      onCompleted: ({ resendWelcomeMailV2 }) => {
        if (isFailure(resendWelcomeMailV2)) {
          return showSnackBar(
            "error",
            `ウェルカムメールの再送に失敗しました。理由: \n${resendWelcomeMailV2.message}`,
          );
        }
        const result = resendWelcomeMailV2 as GQLResendWelcomeMailV2Success;
        showSnackBar(
          "ok",
          `${result.resendEmailAddress}にウェルカムメールを再送しました。`,
        );
      },
      onError: () => {
        showSnackBar("error", "メール送信に失敗しました。");
      },
    });

  const { loading, medicalInstitution, contract, members } =
    useResendWelcomeMailContract(medicalInstitutionId, contractId, {
      withMembers: true,
    });

  const navigate = useNavigate();

  if (loading) {
    return <Loading />;
  }

  // 契約が存在しない・製品管理責任者が未設定の場合は再送できないため医療機関詳細に戻す
  if (!medicalInstitution || !contract || !contract.responsiblePerson) {
    return (
      <Navigate to={`/medicalInstitution/${medicalInstitutionId}`} replace />
    );
  }

  const onSelectPasswordResetMember = (id?: string) => setSelectedMember(id);

  const onClickResendWelcomeMail = () => {
    resendWelcomeEmail({
      variables: {
        input: contract.initial
          ? { contractId, memberId: selectedMemberId }
          : { contractId, reissueIntegrationToken },
      },
    });
  };

  const onCancelButtonClick = () => {
    navigate(`/medicalInstitution/${medicalInstitutionId}`);
  };

  const layoutProps: Omit<ResendWelcomeMailLayoutProps, "children"> = {
    resendWelcomeMailTarget: {
      id: medicalInstitutionId,
      contractId,
      name: medicalInstitution.name,
      address: medicalInstitution.address,
      managerName: contract.responsiblePerson.emailContact.name,
      managerMailAddress: contract.responsiblePerson.emailContact.emailAddress,
      pdt: contract.pdt,
      members,
    },
    onResendWelcomeMailButtonClick: onClickResendWelcomeMail,
    resendWelcomeMailProcessing,
    snackBar,
    onCancelButtonClick,
  };

  // 初回契約はシークレットコード、アカウント統合済みの契約はシークレットトークンを再発行する
  return contract.initial ? (
    <ResendSecretCodeWelcomeMailPage
      {...layoutProps}
      passwordResetMemberId={selectedMemberId}
      onSelectPasswordResetMember={onSelectPasswordResetMember}
    />
  ) : (
    <ResendIntegrationWelcomeMailPage
      {...layoutProps}
      reissueIntegrationToken={reissueIntegrationToken}
      onChangeReissueIntegrationToken={setReissueIntegrationToken}
    />
  );
};
